import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type { AddMethodProps } from "@/modules/billing/choose_method/AddMethodParams.ts";
import type { MethodIconProps } from "@/modules/billing/choose_method/ChooseMethod.tsx";
import type { PaymentProps } from "@/modules/billing/payment/PaymentParams";
import type {
    LinkProcessorId,
    MethodConfig,
    ProcessorConfig,
    ProcessorCurrencyFee,
    ProcessorServiceFee,
    User,
} from "@warrenio/api-spec/spec.oats.gen";
import type { ComponentType, ReactNode } from "react";

/** `${processor.keyword}::${method.key}` */
export type PaymentMethodId = ProcessorConfig["keyword"] | `${LinkProcessorId}::${MethodConfig["key"]}`;

export function getPaymentMethodId(processor: ProcessorConfig, method: MethodConfig | undefined): PaymentMethodId {
    return method != null ? `${processor.keyword as LinkProcessorId}::${method.key}` : processor.keyword;
}

export type MethodFees =
    | {
          service_fee?: ProcessorServiceFee;
          charge_currency_conversion?: ProcessorCurrencyFee;
      }
    | undefined;

/**
 * A central interface for all payment methods, containing their properties and business logic (actions).
 *
 * `link`-type methods are "flattened out" to create 1 of these objects for each method config.
 */
export interface MethodBase {
    type: string;

    id: PaymentMethodId;

    /** Can this payment method be added? */
    canAdd: boolean;
    /** Can this payment method be added to a specific account? `undefined` when creating account */
    canAddToAccount?: ({ account, user }: { account?: EBillingAccount; user?: User }) => boolean;
    /** Is this an automated payment method (supports off-session payments)? */
    isAutomated: boolean;

    fee?: MethodFees;

    name: string;
    icon: (props: MethodIconProps) => ReactNode;

    // Components
    // NB: The functions to actually handle the business logic are exposed by the form components via its ref.

    /** Component to show for "Add method" list.
     * @see {@link AddMethodProps}
     */
    AddForm: ComponentType<AddMethodProps>;

    /** Component to show for payment modal.
     * Must be set if the method supports payments.
     * @see {@link PaymentProps}
     */
    PaymentForm?: unknown;
}

//#region All payment methods

export interface InvoiceMethod extends MethodBase {
    type: "invoice";

    PaymentForm?: never;
}

export interface LinkMethod extends MethodBase {
    type: "link";

    /** Payment can not be started from UI, payment form only displays instructions how to pay */
    isInstructionsOnly?: boolean;

    PaymentForm: ComponentType<PaymentProps>;
}

export interface CardMethod extends MethodBase {
    type: "creditcard";

    PaymentForm: ComponentType<PaymentProps>;
}

/** @see {@link MethodBase} */
export type PaymentMethod = LinkMethod | CardMethod | InvoiceMethod;

//#endregion
